import React, { useEffect, useMemo, createContext } from 'react';
import { isRTLLanguage } from './rtlUtils';

export type Direction = 'ltr' | 'rtl';

export interface RTLContextValue {
    direction: Direction;
    isRTL: boolean;
}

interface RTLProviderProps {
    direction?: Direction;
    languageCode?: string;
    children: React.ReactNode;
}

export const RTLProvider = ({ direction, languageCode, children }: RTLProviderProps) => {
    const resolvedDirection: Direction = direction ?? (languageCode && isRTLLanguage(languageCode) ? 'rtl' : 'ltr');

    const value = useMemo(() => ({ direction: resolvedDirection, isRTL: resolvedDirection === 'rtl' }), [
        resolvedDirection,
    ]);

    useEffect(() => {
        const previousDir = document.documentElement.getAttribute('dir');
        document.documentElement.setAttribute('dir', resolvedDirection);
        return () => {
            if (previousDir) {
                document.documentElement.setAttribute('dir', previousDir);
            } else {
                document.documentElement.removeAttribute('dir');
            }
        };
    }, [resolvedDirection]);

    return <RTLContext.Provider value={value}>{children}</RTLContext.Provider>;
};

export const RTLContext = createContext<RTLContextValue>({
    direction: 'ltr',
    isRTL: false,
});
